const WORK_FORM_SELECTOR = 'form[data-work-form]';
const MESSAGE_SELECTOR = '[data-work-form-message]';
const MESSAGE_TEXT_SELECTOR = '[data-work-form-message-text]';
const SUBMIT_SELECTOR = '[data-work-form-submit]';
const FILE_INPUT_SELECTOR = 'input[name="contact[curriculum]"]';
const LOADING_CLASS = 'is-loading';
const ENDPOINT = '/apps/api/contact-submission';
const DEFAULT_LANGUAGE = 'es';
const DEFAULT_COUNTRY = 'ES';
const DEFAULT_TYPE = 'contact';
const FEEDBACK_ROOT_SELECTOR = '[data-form-feedback-root]';
const FEEDBACK_MESSAGE_SELECTOR = '[data-form-feedback-message]';
const PENDING_DB_NAME = 'garvalin-work-forms';
const PENDING_STORE_NAME = 'pending-submissions';
const PENDING_DB_VERSION = 1;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const DEFAULT_FILE_SIZE_ERROR_MESSAGE = 'El archivo seleccionado supera el tamaño máximo permitido de 10 MB.';

const workForms = Array.from(document.querySelectorAll(WORK_FORM_SELECTOR));

if (workForms.length > 0) {
  initializeFeedbackMessages();
  initializeWorkFormPostedState();
  initializeWorkFormSubmission(workForms);
}

function initializeWorkFormSubmission(forms) {
  for (const form of forms) {
    form.addEventListener('submit', handleWorkFormSubmit, true);

    const fileInput = getFileInput(form);
    if (fileInput instanceof HTMLInputElement) {
      fileInput.addEventListener('change', () => handleFileInputChange(form, fileInput));
    }
  }
}

function handleFileInputChange(form, fileInput) {
  const file = fileInput.files?.[0];
  const errorMessage = form.querySelector(`${MESSAGE_SELECTOR}[data-state="error"]`);
  const fileStatus = form.parentElement?.querySelector('.file-status-text') || form.querySelector('.file-status-text');

  if (!file) {
    return;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    fileInput.value = '';
    const noCvLabel = fileInput.dataset.noCvLabel || 'Ningún archivo seleccionado';
    if (fileStatus instanceof HTMLElement) {
      fileStatus.textContent = noCvLabel;
    }
    const errorMessageText = fileInput.dataset.fileSizeError || DEFAULT_FILE_SIZE_ERROR_MESSAGE;
    setErrorMessage(errorMessage, errorMessageText);
    return;
  }

  hideMessage(errorMessage);
  if (fileStatus instanceof HTMLElement) {
    fileStatus.textContent = file.name;
  }
}

async function handleWorkFormSubmit(event) {
  const form = event.target instanceof HTMLFormElement
    ? event.target.closest(WORK_FORM_SELECTOR)
    : null;

  if (!(form instanceof HTMLFormElement) || form.classList.contains(LOADING_CLASS)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();

  const submitButton = form.querySelector(SUBMIT_SELECTOR);
  const errorMessage = form.querySelector(`${MESSAGE_SELECTOR}[data-state="error"]`);

  hideMessage(errorMessage);

  const fileInput = getFileInput(form);

  if (fileInput instanceof HTMLInputElement && fileInput.files?.[0]) {
    const file = fileInput.files[0];

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const errorMessageText = fileInput.dataset.fileSizeError || DEFAULT_FILE_SIZE_ERROR_MESSAGE;
      setErrorMessage(errorMessage, errorMessageText);
      return;
    }
  }

  setSubmitState(submitButton, true);
  form.classList.add(LOADING_CLASS);

  try {
    await persistPendingSubmission(form);
    setSubmitState(submitButton, false);
    form.classList.remove(LOADING_CLASS);
    submitToShopify(form);
    return;
  } catch (error) {
    console.error('Error sending work form:', error);
    setWorkFormPostedSuccessState(form, false);
    setFormFieldsVisibility(form, true);
    setErrorMessage(errorMessage, error instanceof Error ? error.message : getFormMessage(form, 'errorMessage'));
  } finally {
    setSubmitState(submitButton, false);
    form.classList.remove(LOADING_CLASS);
  }
}

function getSubmittableFields(form) {
  return Array.from(form.elements).filter((field) => {
    return field instanceof HTMLInputElement
      || field instanceof HTMLSelectElement
      || field instanceof HTMLTextAreaElement;
  });
}

function buildPayload(form) {
  const formData = new FormData();
  const name = getFieldValue(form, 'contact[nombre]');
  const surname = getFieldValue(form, 'contact[apellidos]');
  const phone = getFieldValue(form, 'contact[telefono]');
  const email = getFieldValue(form, 'contact[email]');
  const comment = getFieldValue(form, 'contact[comentarios]');
  const customFields = getCustomFields(form);
  const fileInput = getFileInput(form);

  formData.append('country', getCountry());
  formData.append('language', getLanguage());
  formData.append('name', name);
  formData.append('surname', surname);
  formData.append('phone', phone);
  formData.append('email', email);
  formData.append('body', buildBody({
    comment,
    customFields,
  }));
  formData.append('type', getFormType(form));

  if (fileInput instanceof HTMLInputElement) {
    const file = fileInput.files?.[0];

    if (file) {
      formData.append('file', file);
    }
  }

  return formData;
}

function setSubmitState(button, loading) {
  if (!(button instanceof HTMLButtonElement)) {
    return;
  }

  button.disabled = loading;
  button.setAttribute('aria-busy', loading ? 'true' : 'false');

  const idleLabel = button.dataset.idleLabel || '';
  const loadingLabel = button.dataset.loadingLabel || idleLabel;

  if (idleLabel) {
    button.textContent = loading ? loadingLabel : idleLabel;
  }
}

function getFieldValue(form, name) {
  const field = form.querySelector(`[name="${CSS.escape(name)}"]`);

  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
    return '';
  }

  return field.value.trim();
}

function getFileInput(form) {
  const fileInput = form.querySelector(FILE_INPUT_SELECTOR);

  return fileInput instanceof HTMLInputElement ? fileInput : null;
}

function getCustomFields(form) {
  const ignoredNames = new Set([
    'contact[id]',
    'contact[nombre]',
    'contact[apellidos]',
    'contact[telefono]',
    'contact[email]',
    'contact[name]',
    'contact[phone]',
    'contact[body]',
    'contact[curriculum]',
    'contact[comentarios]',
    'contact[privacy]',
    'form_type',
    'utf8',
  ]);

  return getSubmittableFields(form)
    .filter((field) => field.name.startsWith('contact[') && !ignoredNames.has(field.name))
    .map((field) => {
      const key = field.name.replace(/^contact\[/, '').replace(/\]$/, '');

      return {
        label: field.dataset.fieldLabel || key.replace(/-/g, ' '),
        value: field.value.trim(),
      };
    })
    .filter((field) => field.value.length > 0);
}

function getLanguage() {
  return window.Shopify?.locale || document.documentElement.lang || DEFAULT_LANGUAGE;
}

function getCountry() {
  return window.Shopify?.country || document.documentElement.getAttribute('data-country') || DEFAULT_COUNTRY;
}

function getFormType(form) {
  return form.dataset.workFormType || DEFAULT_TYPE;
}

function buildBody({ comment, customFields }) {
  const lines = [];

  for (const field of customFields) {
    lines.push(`${capitalize(field.label)}: ${field.value}`);
  }

  if (comment) {
    lines.push(`Comentarios: ${comment}`);
  }

  return lines.join('\n') || 'Sin comentarios';
}

function submitToShopify(form) {
  const fileInput = getFileInput(form);

  if (fileInput instanceof HTMLInputElement) {
    fileInput.disabled = true;
  }

  setShopifyContactField(
    form,
    'contact[name]',
    [
      getFieldValue(form, 'contact[nombre]'),
      getFieldValue(form, 'contact[apellidos]'),
    ].filter(Boolean).join(' '),
  );
  setShopifyContactField(form, 'contact[phone]', getFieldValue(form, 'contact[telefono]'));
  setShopifyContactField(
    form,
    'contact[body]',
    buildBody({
      comment: getFieldValue(form, 'contact[comentarios]'),
      customFields: getCustomFields(form),
    }),
  );

  form.submit();
}

function setShopifyContactField(form, name, value) {
  if (!value) {
    return;
  }

  let field = form.querySelector(`[name="${CSS.escape(name)}"]`);

  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) {
    field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    form.append(field);
  }

  if (!field.value.trim()) {
    field.value = value;
  }
}

async function parseResponsePayload(response) {
  const responseText = await response.text();

  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return {
      message: responseText,
      ok: response.ok,
    };
  }
}

/**
 * @param {Record<string, unknown> | null} payload
 * @param {string} fallbackMessage
 * @returns {string}
 */
function getErrorMessage(payload, fallbackMessage) {
  if (!payload || typeof payload !== 'object') {
    return fallbackMessage;
  }

  if (payload.errors && typeof payload.errors === 'object') {
    const errorValues = Object.values(payload.errors).flat().filter(Boolean);
    if (errorValues.length > 0) {
      return String(errorValues[0]);
    }
  }

  const candidates = [
    payload.error,
    payload.message,
    payload.data,
    payload.detail,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      if (isHtmlString(candidate)) {
        if (candidate.includes('413 Request Entity Too Large') || candidate.includes('413')) {
          return DEFAULT_FILE_SIZE_ERROR_MESSAGE;
        }

        return 'Se ha producido un error en el servidor al enviar el formulario.';
      }

      return candidate;
    }
  }

  return fallbackMessage;
}

/**
 * @param {HTMLFormElement} form
 * @param {'errorMessage' | 'noFileMessage'} key
 * @returns {string}
 */
function getFormMessage(form, key) {
  if (!(form instanceof HTMLFormElement)) {
    return '';
  }

  return form.dataset[key] || '';
}

function isHtmlString(value) {
  return /<\s*([a-z][a-z0-9]*)\b[^>]*>/i.test(value);
}

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function setFormFieldsVisibility(form, visible) {
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  for (const child of form.children) {
    if (!(child instanceof HTMLElement)) {
      continue;
    }

    if (child.matches(`${MESSAGE_SELECTOR}[data-state="success"]`)) {
      child.hidden = visible;
      continue;
    }

    child.hidden = !visible;
  }
}

function setWorkFormPostedSuccessState(form, postedSuccessfully) {
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  form.toggleAttribute('data-work-form-posted-success', postedSuccessfully);
}

function hideMessage(message) {
  if (!(message instanceof HTMLElement)) {
    return;
  }

  message.hidden = true;
}

function showMessage(message) {
  if (!(message instanceof HTMLElement)) {
    return;
  }

  message.hidden = false;
  focusAndScrollMessage(message);
}

function setErrorMessage(message, text) {
  if (!(message instanceof HTMLElement)) {
    return;
  }

  const textNode = message.querySelector(MESSAGE_TEXT_SELECTOR);

  if (textNode instanceof HTMLElement) {
    textNode.textContent = text;
  }

  showMessage(message);
}

function initializeFeedbackMessages() {
  const revealVisibleFeedback = () => {
    const visibleMessage = document.querySelector(
      `${FEEDBACK_ROOT_SELECTOR} ${FEEDBACK_MESSAGE_SELECTOR}:not([hidden])`
    );

    if (!(visibleMessage instanceof HTMLElement)) {
      return;
    }

    focusAndScrollMessage(visibleMessage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealVisibleFeedback, { once: true });
  } else {
    revealVisibleFeedback();
  }

  window.addEventListener('pageshow', revealVisibleFeedback);
}

function initializeWorkFormPostedState() {
  const revealPostedSuccess = async () => {
    const url = new URL(window.location.href);
    const postedSuccessfully = url.searchParams.get('contact_posted') === 'true';
    const targetFormId = window.location.hash.replace('#', '');

    if (!postedSuccessfully || targetFormId.length === 0) {
      return;
    }

    const form = document.getElementById(targetFormId);

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const successMessage = form.querySelector(`${MESSAGE_SELECTOR}[data-state="success"]`);

    if (!(successMessage instanceof HTMLElement)) {
      return;
    }

    if (form.dataset.middleSubmissionForwarding === 'true') {
      return;
    }

    form.dataset.middleSubmissionForwarding = 'true';
    hideMessage(successMessage);

    try {
      await forwardPendingSubmission(form.id);
      setWorkFormPostedSuccessState(form, true);
      setFormFieldsVisibility(form, false);
      showMessage(successMessage);
    } catch (error) {
      const errorMessage = form.querySelector(`${MESSAGE_SELECTOR}[data-state="error"]`);

      setWorkFormPostedSuccessState(form, false);
      setFormFieldsVisibility(form, true);
      setErrorMessage(errorMessage, error instanceof Error ? error.message : getFormMessage(form, 'errorMessage'));
    } finally {
      delete form.dataset.middleSubmissionForwarding;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealPostedSuccess, { once: true });
  } else {
    revealPostedSuccess();
  }

  window.addEventListener('pageshow', revealPostedSuccess);
}

function focusAndScrollMessage(message) {
  if (!(message instanceof HTMLElement)) {
    return;
  }

  message.focus();
  message.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}

async function persistPendingSubmission(form) {
  const entries = Array.from(buildPayload(form).entries());
  const database = await openPendingSubmissionDatabase();

  await runPendingStoreRequest(database, 'readwrite', (store) => store.put({
    formId: form.id,
    entries,
    createdAt: Date.now(),
  }));
}

async function forwardPendingSubmission(formId) {
  const database = await openPendingSubmissionDatabase();
  const pendingSubmission = await runPendingStoreRequest(database, 'readonly', (store) => store.get(formId));

  if (!pendingSubmission || !Array.isArray(pendingSubmission.entries)) {
    return;
  }

  const formData = new FormData();

  for (const [name, value] of pendingSubmission.entries) {
    formData.append(name, value);
  }

  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  });
  const payload = await parseResponsePayload(response);

  if (!response.ok || payload?.ok === false) {
    throw new Error(getErrorMessage(payload, 'No se ha podido guardar el formulario.'));
  }

  await deletePendingSubmission(formId);
}

async function deletePendingSubmission(formId) {
  const database = await openPendingSubmissionDatabase();

  await runPendingStoreRequest(database, 'readwrite', (store) => store.delete(formId));
}

function openPendingSubmissionDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(PENDING_DB_NAME, PENDING_DB_VERSION);

    request.addEventListener('upgradeneeded', () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(PENDING_STORE_NAME)) {
        database.createObjectStore(PENDING_STORE_NAME, { keyPath: 'formId' });
      }
    });
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener('error', () => reject(request.error), { once: true });
  });
}

function runPendingStoreRequest(database, mode, operation) {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(PENDING_STORE_NAME, mode);
    const request = operation(transaction.objectStore(PENDING_STORE_NAME));
    let result;

    request.addEventListener('success', () => {
      result = request.result;
    }, { once: true });
    request.addEventListener('error', () => reject(request.error), { once: true });
    transaction.addEventListener('complete', () => {
      database.close();
      resolve(result);
    }, { once: true });
    transaction.addEventListener('abort', () => {
      database.close();
      reject(transaction.error);
    }, { once: true });
  });
}
